import { useEffect, useRef } from "react";
import { ActionIcon, Box, Button, Group, Paper, Stack, Switch, Text, Tooltip } from "@mantine/core";
import { visualsFor } from "@nodus/announcements";
import { Volume2, VolumeX } from "lucide-react";
import {
  announcementAudioKey,
  hasAudio,
  planAnnouncementAudio,
  shouldPlayAnnouncementAudio,
  type AnnouncementWithAudio,
} from "@/lib/announcement-audio";
import { useAnnouncementSound } from "@/lib/announcement-sound";

/**
 * Plays one announcement's audio on the kiosk schedule. Renders nothing.
 *
 * Mount it with ``key={announcementAudioKey(a)}``: the effect runs once per
 * raising of the announcement, NOT per refetch — the presigned URL changes on
 * every poll, so it is read through a ref instead of being a dependency.
 * Unmounting (announcement cleared / paused / dismissed, sound muted or
 * turned off) stops playback immediately and cancels every pending repeat.
 */
export function AnnouncementAudioPlayer({ announcement }: { announcement: AnnouncementWithAudio }) {
  const latest = useRef(announcement);
  useEffect(() => {
    latest.current = announcement;
  });
  const key = announcementAudioKey(announcement);

  useEffect(() => {
    const a = latest.current;
    if (!hasAudio(a)) return;
    const plan = planAnnouncementAudio({
      anchor: a.audio.play_at ?? a.raised_at,
      now: Date.now(),
      repeatIntervalSeconds: a.audio.repeat_interval_seconds,
      repeatCount: a.audio.repeat_count,
    });
    if (plan.kind === "skip") return;

    const audio = new Audio(a.audio.url);
    audio.preload = "auto";
    let stopped = false;
    let remaining = plan.plays;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const playOnce = () => {
      if (stopped || remaining <= 0) return;
      remaining -= 1;
      audio.currentTime = 0;
      // Autoplay can still be refused (e.g. the opt-in was given in an earlier
      // session and this tab has had no interaction yet). Fail quietly — the
      // announcement is on screen regardless.
      audio.play().catch(() => undefined);
      if (!plan.chain && remaining > 0) timer = setTimeout(playOnce, plan.intervalMs);
    };
    const onEnded = () => {
      if (plan.chain) playOnce();
    };
    audio.addEventListener("ended", onEnded);
    timer = setTimeout(playOnce, plan.delayMs);

    return () => {
      stopped = true;
      if (timer) clearTimeout(timer);
      audio.removeEventListener("ended", onEnded);
      audio.pause();
      audio.src = "";
    };
  }, [key]);

  return null;
}

/**
 * Sound controls for the announcements currently on screen that have audio.
 *
 * A small fixed card, bottom-right (the corner cards stack bottom-left), above
 * every announcement surface including the edge-to-edge takeover, so it is
 * reachable whatever is showing. It renders nothing when no visible
 * announcement has audio.
 *
 * - Sound not yet enabled in this browser: a hint that sound is available and
 *   the one-time **Enable alarm sounds** button (the click also satisfies the
 *   browser's autoplay rule).
 * - Enabled: one row per audio announcement with a mute / unmute button.
 *   Muting stops that announcement at once and silences its repeats; others
 *   keep playing.
 */
export function AnnouncementSoundPanel({ announcements }: { announcements: AnnouncementWithAudio[] }) {
  const { enabled, setEnabled, isMuted, mute, unmute } = useAnnouncementSound();
  const withAudio = announcements.filter(hasAudio);
  if (withAudio.length === 0) return null;

  return (
    <Paper
      pos="fixed"
      shadow="lg"
      radius="md"
      p="xs"
      withBorder
      maw={320}
      style={{ right: 16, bottom: 88, zIndex: 1100 }}
      aria-label="Announcement sound"
    >
      {!enabled ? (
        <Stack gap={6}>
          <Group gap={6} wrap="nowrap">
            <VolumeX size={16} />
            <Text fz="xs" c="dimmed">
              {withAudio.length === 1 ? "This announcement has sound." : "These announcements have sound."}
            </Text>
          </Group>
          <Button size="xs" leftSection={<Volume2 size={14} />} onClick={() => setEnabled(true)}>
            Enable alarm sounds
          </Button>
        </Stack>
      ) : (
        <Stack gap={4}>
          {withAudio.map((a) => {
            const muted = isMuted(a);
            const title = a.title?.trim() || visualsFor(a.category).label;
            const label = muted ? "Unmute this announcement" : "Mute this announcement";
            return (
              <Group key={a.id} gap="xs" wrap="nowrap" justify="space-between">
                <Box miw={0} flex={1}>
                  <Text fz="xs" fw={600} truncate>
                    {title}
                  </Text>
                  <Text fz="xs" c="dimmed">
                    {muted ? "Muted" : "Sound on"}
                  </Text>
                </Box>
                <Tooltip label={label} withinPortal={false}>
                  <ActionIcon
                    variant={muted ? "subtle" : "light"}
                    color={muted ? "gray" : "blue"}
                    aria-label={`${label}: ${title}`}
                    onClick={() => (muted ? unmute(a) : mute(a))}
                  >
                    {muted ? <VolumeX size={16} /> : <Volume2 size={16} />}
                  </ActionIcon>
                </Tooltip>
              </Group>
            );
          })}
        </Stack>
      )}
    </Paper>
  );
}

/** Mounts one player per announcement that should be audible right now. */
export function AnnouncementAudioPlayers({ announcements }: { announcements: AnnouncementWithAudio[] }) {
  const { enabled, mutedKeys } = useAnnouncementSound();
  return (
    <>
      {announcements
        .filter((a) => shouldPlayAnnouncementAudio(a, { enabled, mutedKeys }))
        .map((a) => (
          <AnnouncementAudioPlayer key={announcementAudioKey(a)} announcement={a} />
        ))}
    </>
  );
}

/** Browser-wide on/off for announcement sounds — lives in the header bell's
 *  popover so a member who enabled sounds can turn them off again. */
export function AnnouncementSoundToggle() {
  const { enabled, setEnabled } = useAnnouncementSound();
  return (
    <Switch
      size="xs"
      label="Alarm sounds in this browser"
      checked={enabled}
      onChange={(e) => setEnabled(e.currentTarget.checked)}
    />
  );
}
